/**
 * StaffCardPaymentPanel — on-session Stripe card-present panel for staff.
 *
 * Used when a customer is physically present and doesn't have a saved card on
 * file (or staff prefer to key in a new card). Mirrors the customer-side
 * PayBookingPanel pattern:
 *   1. POST /api/staff/bookings/:id/payment-intent → { client_secret, stripe_account_id }
 *   2. loadStripe(publishableKey, { stripeAccount }) + mount PaymentElement
 *   3. confirmPayment({ redirect: 'if_required' })
 *   4. On success: call onSuccess(); on error: show inline message + Try again.
 *
 * Modelled on apps/web/src/customer/components/PayBookingPanel.tsx.
 */

import { ApiError, staffGetBookingPaymentIntent } from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { CreditCard, Loader2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Publishable key helper (mirrors customer/lib/stripe_env.ts seam)
// ---------------------------------------------------------------------------

function getStaffStripePublishableKey(): string {
  const envBag = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return envBag?.VITE_STRIPE_PUBLISHABLE_KEY ?? '';
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface StaffCardPaymentPanelProps {
  bookingId: string;
  currency: string;
  /** Called after a successful payment so the parent can refresh and close. */
  onSuccess: () => void;
  /** Called when the user dismisses the panel without completing payment. */
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Phase = 'loading' | 'ready' | 'confirming' | 'error';

export function StaffCardPaymentPanel({
  bookingId,
  currency,
  onSuccess,
  onCancel,
}: StaffCardPaymentPanelProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [amountMinor, setAmountMinor] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);

  // ---------------------------------------------------------------------------
  // Initialise: fetch PI + mount PaymentElement
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setPhase('loading');
      setErrorMsg(null);

      const publishableKey = getStaffStripePublishableKey();
      if (!publishableKey) {
        setErrorMsg('Stripe is not configured. Check VITE_STRIPE_PUBLISHABLE_KEY.');
        setPhase('error');
        return;
      }

      // 1. Fetch a fresh PaymentIntent for the outstanding balance.
      let piData: {
        client_secret: string;
        stripe_account_id: string;
        amount_minor: number;
        currency: string;
      };
      try {
        piData = await staffGetBookingPaymentIntent(bookingId);
      } catch (err) {
        if (cancelled) return;
        const code = err instanceof ApiError ? err.code : null;
        const friendly =
          code === 'no_outstanding_balance'
            ? 'No outstanding balance — nothing to charge.'
            : code === 'billing_not_configured'
              ? 'Stripe is not configured for this account.'
              : code === 'connect_off'
                ? 'Stripe Connect is not enabled for this account.'
                : `Could not create payment (${code ?? 'unknown'}).`;
        setErrorMsg(friendly);
        setPhase('error');
        return;
      }

      if (cancelled) return;
      setAmountMinor(piData.amount_minor);

      // 2. Load Stripe.js scoped to the tenant's connected account.
      const stripe = await loadStripe(publishableKey, {
        stripeAccount: piData.stripe_account_id,
      });
      if (cancelled || !stripe) {
        setPhase('error');
        setErrorMsg('Failed to load Stripe.js.');
        return;
      }
      stripeRef.current = stripe;

      // 3. Mount the Payment Element.
      const elements = stripe.elements({
        clientSecret: piData.client_secret,
        appearance: { theme: 'stripe' },
      });
      elementsRef.current = elements;

      const paymentElement = elements.create('payment');
      if (mountRef.current) {
        paymentElement.mount(mountRef.current);
      }
      if (!cancelled) setPhase('ready');
    }

    void init();

    return () => {
      cancelled = true;
      // Unmount any mounted elements on teardown.
      if (elementsRef.current) {
        try {
          elementsRef.current.getElement('payment')?.unmount();
        } catch {
          // ignore
        }
      }
      stripeRef.current = null;
      elementsRef.current = null;
    };
    // bookingId changes should trigger a fresh PI.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  // ---------------------------------------------------------------------------
  // Confirm
  // ---------------------------------------------------------------------------

  async function handleConfirm() {
    if (!stripeRef.current || !elementsRef.current) return;
    setPhase('confirming');
    setErrorMsg(null);

    const { error } = await stripeRef.current.confirmPayment({
      elements: elementsRef.current,
      confirmParams: {
        // We never redirect for a staff-side booking payment.
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMsg(
        error.message ?? 'Card payment failed. Please check the card details and try again.',
      );
      setPhase('ready');
      return;
    }

    // Success — let the parent know so it can refresh balances and close.
    onSuccess();
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <CreditCard className="size-3.5 text-muted-foreground" strokeWidth={1.75} />
          <p className="text-xs font-medium text-foreground">Take card payment</p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="rounded p-1 text-muted-foreground hover:bg-secondary"
          aria-label="Cancel card payment"
        >
          <X className="size-3.5" strokeWidth={1.75} />
        </button>
      </div>

      {/* Amount */}
      {amountMinor > 0 && (
        <p className="text-xs text-muted-foreground">
          Charging{' '}
          <span className="num font-semibold text-foreground">
            {formatPriceMinor(amountMinor, currency)}
          </span>{' '}
          outstanding.
        </p>
      )}

      {/* Stripe Payment Element mount point */}
      {phase !== 'error' && (
        <div
          ref={mountRef}
          className="rounded-md border border-border bg-background p-3 min-h-[80px]"
        >
          {phase === 'loading' && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
              Loading payment form…
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {errorMsg && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {errorMsg}
        </p>
      )}

      {/* Actions */}
      {phase !== 'error' && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={phase !== 'ready'}
            onClick={() => void handleConfirm()}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {phase === 'confirming' ? (
              <>
                <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
                Processing…
              </>
            ) : (
              <>
                <CreditCard className="size-3.5" strokeWidth={1.75} />
                Charge card
              </>
            )}
          </button>
        </div>
      )}

      {/* Retry after PI-level error (e.g. no outstanding balance) */}
      {phase === 'error' && (
        <button
          type="button"
          onClick={onCancel}
          className="w-full rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-secondary"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}
